<template>
	<div class="l-content-section -header">
		<div class="jumbotron-compact">
			<div class="l-container">
				<div class="jumbotron-compact__inner-w">
					<div class="jumbotron-compact__content">
						<h1 class="jumbotron-compact__title">
							{{ title }}
						</h1>
						<div
							v-if="subtitle || secondarySubtitle || link"
							class="jumbotron-compact__meta"
						>
							<a
								v-if="subtitle && subtitleLink"
								class="jumbotron-compact__subtitle -linked"
								:href="subtitleLink"
							>
								{{ subtitle }}
							</a>
							<span
								v-else-if="subtitle"
								class="jumbotron-compact__subtitle"
							>
								{{ subtitle }}
							</span>
							<span
								v-if="secondarySubtitle"
								class="jumbotron-compact__meta-item"
							>
								{{ secondarySubtitle }}
							</span>
							<a
								v-if="link"
								class="jumbotron-compact__subtitle -linked"
								:href="link"
								:target="isExternalLink ? '_blank' : null"
								:rel="isExternalLink ? 'noopener noreferrer' : null"
							>
								{{ linkText }}
							</a>
						</div>
					</div>
					<img
						v-if="logoSource"
						:key="logoSource"
						class="jumbotron-compact__logo"
						:alt="title"
						:src="logoSource"
						@error="handleLogoError"
					>
				</div>
			</div>
		</div>
		<slot name="after" />
	</div>
</template>

<script>
export default {
	name:  'JumbotronCompact',
	props: {
		title: {
			type:     String,
			required: true,
		},
		subtitle: {
			type:    String,
			default: null,
		},
		secondarySubtitle: {
			type:    String,
			default: null,
		},
		subtitleLink: {
			type:    String,
			default: null,
		},
		link: {
			type:    String,
			default: null,
		},
		linkText: {
			type:    String,
			default: null,
		},
		logoUrl: {
			type:    String,
			default: null,
		},
	},
	data() {
		return {
			useOriginalLogo: false,
			logoFailed:      false,
		};
	},
	computed: {
		// Keep the original available when the resize service cannot serve a derivative.
		logoSource() {
			if (!this.logoUrl || this.logoFailed) return null;

			return this.useOriginalLogo ? this.logoUrl : this.$Helpers.getScaledImageUrl(this.logoUrl, 200);
		},
		isExternalLink() {
			return this.link && (new URL(this.link, location.origin)).hostname !== location.hostname;
		},
	},
	watch: {
		// A replacement logo must get a fresh attempt after the previous one failed.
		logoUrl() {
			this.useOriginalLogo = false;
			this.logoFailed = false;
		},
	},
	methods: {
		// Retry the original once, then omit unavailable logos instead of showing a broken image.
		handleLogoError() {
			if (!this.useOriginalLogo && this.logoSource !== this.logoUrl) {
				this.useOriginalLogo = true;
				return;
			}

			this.logoFailed = true;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.jumbotron-compact {
	background-color: var(--color-grey-900);
	color: var(--color-soft-white);
}

.jumbotron-compact__inner-w {
	display: flex;
	align-items: center;
	justify-content: space-between;
	min-height: 116px;
	padding-top: $sp4;
	padding-bottom: $sp4;
	gap: $sp5;

	@include size(md-up) {
		min-height: 140px;
		padding-top: $sp5;
		padding-bottom: $sp5;
	}
}

.jumbotron-compact__content {
	min-width: 0;
}

.jumbotron-compact__title {
	@include font(primary, bold, normal, fs-170);

	body.-t-light & { // stylelint-disable-line
		color: var(--color-grey-300);
	}
}

.jumbotron-compact__meta {
	@include font(primary, book, normal, fs-100);

	display: flex;
	flex-wrap: wrap;
	align-items: center;
	margin-top: $sp2;
	column-gap: $sp3;
	row-gap: $sp2;
}

.jumbotron-compact__subtitle {
	display: flex;
	align-items: center;
	color: var(--color-soft-white);

	&.-linked {
		color: var(--color-hype-yellow);

		&:focus,
		&:hover {
			text-decoration: underline;
		}
	}

	&:not(:first-child)::before {
		display: block;
		margin-right: $sp3;
		width: 4px;
		height: 4px;
		background-color: var(--color-soft-white);
		border-radius: 50%;
		content: '';

		body.-t-light & { // stylelint-disable-line
			background-color: var(--color-grey-400);
		}
	}

	body.-t-light & { // stylelint-disable-line
		color: var(--color-grey-400);
	}
}

.jumbotron-compact__meta-item {
	display: flex;
	align-items: center;

	&:not(:first-child)::before {
		display: block;
		margin-right: $sp3;
		width: 4px;
		height: 4px;
		background-color: var(--color-soft-white);
		border-radius: 50%;
		content: '';

		body.-t-light & { // stylelint-disable-line
			background-color: var(--color-grey-400);
		}
	}

	body.-t-light & { // stylelint-disable-line
		color: var(--color-grey-400);
	}
}

.jumbotron-compact__logo {
	flex-shrink: 0;
	width: auto;
	max-width: 80px;
	max-height: 72px;
	object-fit: contain;

	@include size(md-up) {
		max-width: 120px;
		max-height: 96px;
	}
}
</style>
